/**
 * Cross-page draft state for Salary Template Creation Pipeline
 * (Components hub → Template → Allocate → Formulas → Review).
 */

export const PIPELINE_STORAGE_KEY = "brixigo.salaryTemplatePipeline";
export const ADVANCED_MODE_KEY = "brixigo.salaryComponents.advancedMode";

export const PIPELINE_STEPS = {
  template: 1,
  components: 2,
  formulas: 3,
  ctcAllocation: 4,
  review: 5,
};

const EMPTY = {
  active: false,
  currentStep: PIPELINE_STEPS.template,
  returnToReview: false,
  templateId: null,
  templateName: "",
  templateDescription: "",
  allocatedComponentIds: [],
  formulas: {},
  targetAnnualCtc: null,
  draftEmployeeAssignments: [],
  editMode: false,
};

function migratePipelineState(parsed) {
  if (!parsed || typeof parsed !== "object") return parsed;
  const version = Number(parsed._pipelineVersion) || 1;
  if (version < 2 && parsed.currentStep === 4) {
    return { ...parsed, currentStep: PIPELINE_STEPS.review, _pipelineVersion: 2 };
  }
  if (version < 2) {
    return { ...parsed, _pipelineVersion: 2 };
  }
  return parsed;
}

function readStore(store) {
  try {
    const raw = store.getItem(PIPELINE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return { ...EMPTY, ...migratePipelineState(parsed) };
  } catch {
    return null;
  }
}

export function readPipelineState() {
  return readStore(sessionStorage) || readStore(localStorage) || { ...EMPTY };
}

export function writePipelineState(patch = {}) {
  const prev = readPipelineState();
  const next = {
    ...prev,
    ...patch,
    allocatedComponentIds: Array.isArray(patch.allocatedComponentIds)
      ? [...new Set(patch.allocatedComponentIds.map(String))]
      : prev.allocatedComponentIds,
    formulas:
      patch.formulas && typeof patch.formulas === "object"
        ? { ...prev.formulas, ...patch.formulas }
        : prev.formulas,
    draftEmployeeAssignments: Array.isArray(patch.draftEmployeeAssignments)
      ? patch.draftEmployeeAssignments
      : prev.draftEmployeeAssignments,
  };
  const payload = JSON.stringify(next);
  try {
    sessionStorage.setItem(PIPELINE_STORAGE_KEY, payload);
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(PIPELINE_STORAGE_KEY, payload);
  } catch {
    /* ignore */
  }
  return next;
}

export function clearPipelineState() {
  try {
    sessionStorage.removeItem(PIPELINE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(PIPELINE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function startSalaryTemplatePipeline() {
  clearPipelineState();
  return writePipelineState({
    ...EMPTY,
    active: true,
    currentStep: PIPELINE_STEPS.template,
    _pipelineVersion: 2,
  });
}

export function isPipelineActive(state = readPipelineState()) {
  return Boolean(state?.active);
}

/** True only while the create/edit wizard itself is on screen. */
export function isPipelineLocation(pathname, search = "") {
  const raw = String(search || "");
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const step = parsePipelineStep(params);
  if (step == null) return false;
  const path = (pathname || "/").replace(/\/+$/, "") || "/";
  if (step === PIPELINE_STEPS.formulas) return path === "/salary-v2/formula-builder";
  return path === STRUCTURES_PAGE;
}

export function parsePipelineStep(searchParams) {
  const raw = searchParams?.get?.("pipeline");
  if (!raw) return null;
  const n = Number(raw);
  if (n >= 1 && n <= 5) return n;
  return null;
}

export function pipelineQueryStep(step) {
  return `?pipeline=${step}`;
}

const STRUCTURES_PAGE = "/salary-v2/structures";

export function pipelineStepPath(step, state = readPipelineState()) {
  switch (step) {
    case PIPELINE_STEPS.template:
      return `${STRUCTURES_PAGE}${pipelineQueryStep(PIPELINE_STEPS.template)}`;
    case PIPELINE_STEPS.components:
      if (!String(state.templateName || "").trim()) {
        return `${STRUCTURES_PAGE}${pipelineQueryStep(PIPELINE_STEPS.template)}`;
      }
      return `${STRUCTURES_PAGE}${pipelineQueryStep(PIPELINE_STEPS.components)}`;
    case PIPELINE_STEPS.formulas:
      return `/salary-v2/formula-builder${pipelineQueryStep(PIPELINE_STEPS.formulas)}`;
    case PIPELINE_STEPS.ctcAllocation:
      return `${STRUCTURES_PAGE}${pipelineQueryStep(PIPELINE_STEPS.ctcAllocation)}`;
    case PIPELINE_STEPS.review:
      return `${STRUCTURES_PAGE}${pipelineQueryStep(PIPELINE_STEPS.review)}`;
    default:
      return STRUCTURES_PAGE;
  }
}

export function readAdvancedModeEnabled() {
  try {
    return localStorage.getItem(ADVANCED_MODE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeAdvancedModeEnabled(on) {
  try {
    if (on) localStorage.setItem(ADVANCED_MODE_KEY, "1");
    else localStorage.removeItem(ADVANCED_MODE_KEY);
  } catch {
    /* ignore */
  }
}

export function splitAllocatedIds(componentRows = [], allocatedIds = []) {
  const idSet = new Set((allocatedIds || []).map(String));
  const earnings = [];
  const deductions = [];
  for (const row of componentRows || []) {
    const id = String(row.component_id);
    if (!idSet.has(id)) continue;
    const cat = String(row.component_category || row.component_type || "").toLowerCase();
    if (cat.startsWith("earn") || cat === "benefit") earnings.push(id);
    else deductions.push(id);
  }
  return { earnings, deductions, all: [...idSet] };
}

/**
 * Client-side validation before final template creation.
 */
export function validatePipelineForSubmit(state, componentRows = []) {
  const errors = [];
  const name = String(state.templateName || "").trim();
  if (!name) errors.push("Salary structure name is required.");

  const ids = [...new Set((state.allocatedComponentIds || []).map(String))];
  if (!ids.length) errors.push("Select at least one component for this salary structure.");

  const rowById = new Map((componentRows || []).map((r) => [String(r.component_id), r]));
  let missing = false;
  for (const id of ids) {
    if (!rowById.get(id)) missing = true;
  }
  if (missing) {
    errors.push("A selected component is no longer in payroll. Open Components, keep the ones you still need, and save again.");
  }

  return { ok: errors.length === 0, errors };
}
